import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { toast } from "sonner";
import {
  Car, Home, Calculator, Loader2, Plus, Trash2, CheckCircle2,
  AlertTriangle, Send,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { useSeedBackTo } from "@/hooks/useSeedBackTo";
import BiSelect from "@/components/BiSelect";
import RulesetStamp from "@/components/tax/RulesetStamp";
import { AtoDisclaimer } from "@/components/tax/AtoDisclaimer";
import { useApp } from "@/contexts/AppContext";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";

/**
 * Vehicle & WFH — Module 4 helper.
 *
 * A running LOG of trip km + home-office hours. Estimates use ATO
 * cents-per-km + WFH fixed-rate under the active ruleset. Estimates
 * are ADVISORY: nothing hits the ledger until the tradie explicitly
 * taps "Confirm & post to ledger", which writes ONE locked entry
 * flagged `is_estimate=true, estimate_kind="vehicle_cpk"|"wfh_fixed"`.
 *
 * Re-posting for the same FY replaces the previous estimate row so we
 * never stack duplicates.
 */
export default function VehicleWfhPage() {
  const [tab, setTab] = useState("vehicle");
  const currentFy = useMemo(() => {
    const now = new Date();
    return now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
  }, []);
  const [fy, setFy] = useState(currentFy);
  const fyLabel = `FY${String(fy).slice(-2)}`;

  return (
    <div className="space-y-4 pb-24" data-testid="vehicle-wfh-page">
      <PageHeader
        icon={Calculator}
        title="Vehicle & Home Office"
        subtitle={`Log km & hours · ${fyLabel} · Estimate only until you post`}
        testid="vw-header"
        backTo="/tax"
      />

      <div className="flex items-center gap-2">
        <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading">FY</label>
        <BiSelect
          data-testid="vw-fy-picker"
          className="bi-input h-9 py-0 text-sm"
          value={fy}
          onChange={(e) => setFy(Number(e.target.value))}
        >
          {[currentFy + 1, currentFy, currentFy - 1, currentFy - 2].map((y) => (
            <option key={y} value={y}>{`FY${String(y).slice(-2)}`}</option>
          ))}
        </BiSelect>
      </div>

      <AtoDisclaimer context="tax_summary" compact testId="vw-disclaimer" />

      <div className="grid grid-cols-2 gap-1 rounded-xl bg-white/5 p-1" data-testid="vw-tabs">
        <TabBtn active={tab === "vehicle"} onClick={() => setTab("vehicle")} testid="tab-vehicle">
          <Car className="w-3.5 h-3.5" /> Vehicle km
        </TabBtn>
        <TabBtn active={tab === "wfh"} onClick={() => setTab("wfh")} testid="tab-wfh">
          <Home className="w-3.5 h-3.5" /> Home office
        </TabBtn>
      </div>

      {tab === "vehicle" ? <VehiclePanel fy={fy} /> : <WfhPanel fy={fy} />}

      <div className="text-center pt-2" data-testid="vw-ruleset-footer">
        <RulesetStamp />
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`h-10 rounded-lg text-[11px] font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5 transition ${
        active
          ? "bg-[#FF9500] text-white"
          : "text-white/50 hover:text-white/90"
      }`}
    >
      {children}
    </button>
  );
}

function fmtMoney(n) {
  return `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDateAu(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  } catch { return iso; }
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

/* -------------------------------------------------------------------- */
/* Vehicle panel                                                        */
/* -------------------------------------------------------------------- */
function VehiclePanel({ fy }) {
  const { jobs } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tripDate, setTripDate] = useState(todayIso());
  const [km, setKm] = useState("");
  const [purpose, setPurpose] = useState("");
  const [jobId, setJobId] = useState("");
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/logs/vehicle?fy=${fy}`);
      setData(res);
    } catch (err) {
      devLog.warn("vehicle logs load failed", err);
      toast.error("Couldn't load km log");
    } finally {
      setLoading(false);
    }
  }, [fy]);

  useEffect(() => { load(); }, [load]);

  const addLog = async (e) => {
    e?.preventDefault();
    const kmNum = Number(km);
    if (!kmNum || kmNum <= 0) {
      toast.error("Enter kilometres");
      return;
    }
    setSaving(true);
    try {
      await api.post("/logs/vehicle", {
        km: kmNum,
        trip_date: tripDate || todayIso(),
        purpose: purpose.trim(),
        job_id: jobId || null,
      });
      setKm("");
      setPurpose("");
      await load();
      toast.success(`Logged ${kmNum} km`);
    } catch (err) {
      devLog.warn("add vehicle log failed", err);
      toast.error(err.response?.data?.detail || "Couldn't add log");
    } finally {
      setSaving(false);
    }
  };

  const deleteLog = async (id) => {
    try {
      await api.delete(`/logs/vehicle/${id}`);
      await load();
    } catch (err) {
      devLog.warn("delete vehicle log failed", err);
      toast.error("Couldn't delete log");
    }
  };

  const post = async () => {
    setPosting(true);
    try {
      const { data: res } = await api.post("/logs/post-to-ledger", { kind: "vehicle_cpk", fy });
      toast.success(`Posted ${fmtMoney(res.estimate)} vehicle estimate to your ledger`);
      setConfirmOpen(false);
      await load();
    } catch (err) {
      devLog.warn("post to ledger failed", err);
      toast.error(err.response?.data?.detail || "Couldn't post estimate");
    } finally {
      setPosting(false);
    }
  };

  if (loading) return <LoadingBlock />;

  return (
    <div className="space-y-4">
      <EstimateBox
        label="Vehicle deduction estimate"
        amount={data?.estimate}
        working={data?.working}
        capped={data?.capped}
        secondary={`${(data?.total_km || 0).toLocaleString("en-AU")} km logged · ${data?.rate_cents_per_km || 91}c/km`}
        testid="veh-estimate"
      />

      <form onSubmit={addLog} className="bi-surface p-3 space-y-2" data-testid="veh-add-form">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading mb-1">
          Log a trip
        </div>
        <div className="grid grid-cols-3 gap-2">
          <input
            type="date"
            data-testid="veh-input-date"
            className="bi-input col-span-1"
            value={tripDate}
            onChange={(e) => setTripDate(e.target.value)}
          />
          <input
            type="number"
            inputMode="decimal"
            placeholder="km"
            data-testid="veh-input-km"
            className="bi-input col-span-1"
            value={km}
            onChange={(e) => setKm(e.target.value)}
            min="0"
            step="0.1"
          />
          <BiSelect
            data-testid="veh-input-job"
            className="bi-input col-span-1 h-10 py-0 text-sm"
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
          >
            <option value="">No job</option>
            {(jobs || []).map((j) => (
              <option key={j.id} value={j.id}>{j.name}</option>
            ))}
          </BiSelect>
        </div>
        <input
          type="text"
          placeholder="Purpose (e.g. Bunnings run, site visit)"
          data-testid="veh-input-purpose"
          className="bi-input w-full"
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          maxLength={140}
        />
        <button
          type="submit"
          data-testid="veh-add-btn"
          disabled={saving}
          className="w-full h-10 rounded-lg bi-accent-btn text-sm font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add trip
        </button>
      </form>

      <LogsList
        testid="veh-logs"
        logs={data?.logs || []}
        formatLine={(row) => `${fmtDateAu(row.trip_date)} · ${row.km} km${row.purpose ? ` · ${row.purpose}` : ""}`}
        onDelete={(row) => setConfirmDel({ id: row.id, label: `${row.km} km on ${fmtDateAu(row.trip_date)}` })}
      />

      <PostRow
        disabled={!(data?.estimate > 0)}
        onClick={() => setConfirmOpen(true)}
        testid="veh-post"
        label={`Post ${fmtMoney(data?.estimate || 0)} to ledger`}
        posting={posting}
        alreadyPostedCount={(data?.logs || []).filter((l) => l.posted_entry_id).length}
        totalCount={(data?.logs || []).length}
      />

      <ConfirmDeleteDialog
        open={!!confirmDel}
        onCancel={() => setConfirmDel(null)}
        title="Delete km log?"
        description={confirmDel ? `Remove ${confirmDel.label} from your log?` : ""}
        onConfirm={async () => {
          if (confirmDel) await deleteLog(confirmDel.id);
          setConfirmDel(null);
        }}
      />

      <ConfirmPostDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        kind="vehicle_cpk"
        amount={data?.estimate}
        detail={data?.working}
        posting={posting}
        onConfirm={post}
      />
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* WFH panel                                                            */
/* -------------------------------------------------------------------- */
function WfhPanel({ fy }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logDate, setLogDate] = useState(todayIso());
  const [hours, setHours] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/logs/wfh?fy=${fy}`);
      setData(res);
    } catch (err) {
      devLog.warn("wfh logs load failed", err);
      toast.error("Couldn't load WFH log");
    } finally {
      setLoading(false);
    }
  }, [fy]);

  useEffect(() => { load(); }, [load]);

  const addLog = async (e) => {
    e?.preventDefault();
    const hoursNum = Number(hours);
    if (!hoursNum || hoursNum <= 0) {
      toast.error("Enter hours");
      return;
    }
    setSaving(true);
    try {
      await api.post("/logs/wfh", {
        hours: hoursNum,
        log_date: logDate || todayIso(),
        notes: notes.trim(),
      });
      setHours("");
      setNotes("");
      await load();
      toast.success(`Logged ${hoursNum} hrs`);
    } catch (err) {
      devLog.warn("add wfh log failed", err);
      toast.error(err.response?.data?.detail || "Couldn't add hours");
    } finally {
      setSaving(false);
    }
  };

  const deleteLog = async (id) => {
    try {
      await api.delete(`/logs/wfh/${id}`);
      await load();
    } catch (err) {
      devLog.warn("delete wfh log failed", err);
      toast.error("Couldn't delete log");
    }
  };

  const post = async () => {
    setPosting(true);
    try {
      const { data: res } = await api.post("/logs/post-to-ledger", { kind: "wfh_fixed", fy });
      toast.success(`Posted ${fmtMoney(res.estimate)} home-office estimate to your ledger`);
      setConfirmOpen(false);
      await load();
    } catch (err) {
      devLog.warn("post wfh to ledger failed", err);
      toast.error(err.response?.data?.detail || "Couldn't post estimate");
    } finally {
      setPosting(false);
    }
  };

  if (loading) return <LoadingBlock />;

  return (
    <div className="space-y-4">
      <EstimateBox
        label="Home office deduction estimate"
        amount={data?.estimate}
        working={data?.working}
        secondary={`${(data?.total_hours || 0).toLocaleString("en-AU")} hrs logged · ${Math.round((data?.rate_aud_per_hour || 0.70) * 100)}c/hr`}
        testid="wfh-estimate"
      />

      <form onSubmit={addLog} className="bi-surface p-3 space-y-2" data-testid="wfh-add-form">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading mb-1">
          Log home-office hours
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="date"
            data-testid="wfh-input-date"
            className="bi-input"
            value={logDate}
            onChange={(e) => setLogDate(e.target.value)}
          />
          <input
            type="number"
            inputMode="decimal"
            placeholder="hours"
            data-testid="wfh-input-hours"
            className="bi-input"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            min="0"
            max="24"
            step="0.25"
          />
        </div>
        <input
          type="text"
          placeholder="What did you do? (quotes, invoicing, admin…)"
          data-testid="wfh-input-notes"
          className="bi-input w-full"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={140}
        />
        <button
          type="submit"
          data-testid="wfh-add-btn"
          disabled={saving}
          className="w-full h-10 rounded-lg bi-accent-btn text-sm font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add hours
        </button>
      </form>

      <LogsList
        testid="wfh-logs"
        logs={data?.logs || []}
        formatLine={(row) => `${fmtDateAu(row.log_date)} · ${row.hours} hrs${row.notes ? ` · ${row.notes}` : ""}`}
        onDelete={(row) => setConfirmDel({ id: row.id, label: `${row.hours} hrs on ${fmtDateAu(row.log_date)}` })}
      />

      <PostRow
        disabled={!(data?.estimate > 0)}
        onClick={() => setConfirmOpen(true)}
        testid="wfh-post"
        label={`Post ${fmtMoney(data?.estimate || 0)} to ledger`}
        posting={posting}
        alreadyPostedCount={(data?.logs || []).filter((l) => l.posted_entry_id).length}
        totalCount={(data?.logs || []).length}
      />

      <ConfirmDeleteDialog
        open={!!confirmDel}
        onCancel={() => setConfirmDel(null)}
        title="Delete hours log?"
        description={confirmDel ? `Remove ${confirmDel.label} from your log?` : ""}
        onConfirm={async () => {
          if (confirmDel) await deleteLog(confirmDel.id);
          setConfirmDel(null);
        }}
      />

      <ConfirmPostDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        kind="wfh_fixed"
        amount={data?.estimate}
        detail={data?.working}
        posting={posting}
        onConfirm={post}
      />
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Shared sub-components                                                 */
/* -------------------------------------------------------------------- */
function LoadingBlock() {
  return (
    <div className="bi-surface p-8 flex items-center justify-center text-white/50 gap-2" data-testid="vw-loading">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>
  );
}

function EstimateBox({ label, amount, working, secondary, capped, testid }) {
  return (
    <div className="bi-surface p-4 space-y-1 border border-[#FF9500]/25" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-heading">
          {label}
        </div>
        <span
          className="text-[9px] uppercase tracking-wider font-heading font-bold text-[#FBBF24] bg-[#FBBF24]/12 border border-[#FBBF24]/25 px-1.5 py-0.5 rounded-full"
          data-testid={`${testid}-flag`}
        >
          Estimate only
        </span>
      </div>
      <div className="font-heading font-extrabold text-3xl text-[#FF9500] tabular-nums" data-testid={`${testid}-amount`}>
        {fmtMoney(amount || 0)}
      </div>
      {secondary && (
        <div className="text-[11px] text-white/60">{secondary}</div>
      )}
      {capped && (
        <div className="text-[11px] text-[#FBBF24] inline-flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> Capped at 5,000 km/year
        </div>
      )}
      {working && (
        <div className="text-[10px] text-white/40 pt-1 leading-snug">{working}</div>
      )}
    </div>
  );
}

function LogsList({ logs, formatLine, onDelete, testid }) {
  if (!logs.length) {
    return (
      <div className="bi-surface p-4 text-[12px] text-white/40 text-center" data-testid={`${testid}-empty`}>
        No entries yet — add your first above.
      </div>
    );
  }
  return (
    <div className="bi-surface p-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading px-2 py-1.5">
        Recent · {logs.length}
      </div>
      <div className="divide-y divide-white/5">
        {logs.slice(0, 30).map((row) => (
          <div
            key={row.id}
            className="flex items-center gap-2 px-2 py-2"
            data-testid={`${testid}-row-${row.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="text-[12px] text-white/85 truncate">{formatLine(row)}</div>
              {row.posted_entry_id && (
                <div className="text-[9.5px] text-[#34D399] inline-flex items-center gap-1 mt-0.5">
                  <CheckCircle2 className="w-3 h-3" /> Posted to ledger
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => onDelete(row)}
              data-testid={`${testid}-delete-${row.id}`}
              className="shrink-0 p-1.5 rounded hover:bg-white/10 text-white/40 hover:text-[#F87171]"
              aria-label="Delete log"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function PostRow({ disabled, onClick, testid, label, posting, alreadyPostedCount, totalCount }) {
  const alreadyPosted = alreadyPostedCount > 0 && alreadyPostedCount === totalCount;
  return (
    <div className="sticky bottom-20 sm:bottom-24 pt-1 z-20 space-y-1">
      <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        disabled={disabled || posting}
        className="w-full h-14 rounded-xl bi-accent-btn text-sm font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 shadow-[0_10px_30px_-15px_rgba(255,149,0,0.6)] backdrop-blur disabled:opacity-60"
      >
        {posting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        {label}
      </button>
      <div className="text-[10px] text-white/40 text-center px-2">
        {alreadyPosted
          ? "Already posted — posting again will replace the current ledger row."
          : "Nothing hits your ledger until you confirm."}
      </div>
    </div>
  );
}

function ConfirmPostDialog({ open, onOpenChange, kind, amount, detail, posting, onConfirm }) {
  if (!open) return null;
  const label = kind === "vehicle_cpk" ? "vehicle km" : "home office";
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={() => !posting && onOpenChange(false)}
      data-testid="vw-confirm-post"
    >
      <div
        className="w-full max-w-md bi-surface p-4 rounded-2xl border border-[#FF9500]/40"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF9500] font-heading">
          Confirm & post to ledger
        </div>
        <div className="font-heading font-extrabold text-2xl text-white mt-1">
          {fmtMoney(amount || 0)}
        </div>
        <div className="text-[11px] text-white/60 mt-1 leading-snug">
          Adds a single locked <b>{label}</b> deduction row to your ledger. It will show as an
          <span className="text-[#FBBF24]"> estimate</span> in your pack and reports.
        </div>
        {detail && (
          <div className="text-[10px] text-white/40 mt-2 leading-snug">{detail}</div>
        )}
        <div className="grid grid-cols-2 gap-2 mt-4">
          <button
            type="button"
            data-testid="vw-confirm-cancel"
            onClick={() => onOpenChange(false)}
            disabled={posting}
            className="h-11 rounded-xl bi-ghost-btn text-sm font-heading font-bold uppercase tracking-wider disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="vw-confirm-post-btn"
            onClick={onConfirm}
            disabled={posting}
            className="h-11 rounded-xl bi-accent-btn text-sm font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Confirm & post
          </button>
        </div>
      </div>
    </div>
  );
}
